import {$qs, $qsAll, onWindowDataFetch, isMobile} from '../../@shared/ts/dom';
import {captureSentryException} from '../../@shared/ts/sentry';
import {type WCPHPData} from '../../@type/woocommerce/php-data';
import {peachpay_placeButtonMiniCart, peachpay_isProductPage} from './product-page';
import {peachpay_getShortCodeAddToCartFormData} from './shortcode';
import {getPluginLocaleText, languageCodeToLocale} from './translations';
import {type ICurrencySwitcherFeatureConfig} from '../../express-checkout/ts/models/ICurrencySwitcherFeatureConfig';
import {peachpayModalHTMLTemplate} from './templates/modal';

let loaded = false;

let buttonClickedBeforeLoad = false;
let buttonClickedBeforeLoadID = '';
let buttonClickedBeforeLoadIsShortcodeProduct = false;
let buttonClickedBeforeLoadProductID = 0;

let productButtonIsSpinning = false;

let shortCodeButtonIsSpinning = false;
let shortCodeButtonSpinningID = 0;

const selectedOutOfStockItems = new Set<number>();
const pendingVariationDetailRequests = 0;

declare const peachpay_data: WCPHPData;

export interface IButtonConfigurationOptions {
	width?: string | null;
	isShortcode?: boolean;
	isFloatingButton?: boolean;
	isMiniCart?: boolean;
	isElementor?: boolean;
	alignment?: string | null;
	borderRadius?: number;
	clickID?: string;
	shortcodeInfo?: {
		shortcodeId: number;
		productId: number;
		quantity: number;
		isElementorSingleProductTemplate?: boolean;
	};
}

// In order to make sure the mini cart is always placed put this here and it on
// loading the window will always place it.
if (peachpay_data.hide_mini_cart !== '1') {
	window.addEventListener('DOMContentLoaded', () => {
		peachpay_placeButtonMiniCart();
		jQuery(document.body).on(
			'wc_fragments_loaded wc_fragments_refreshed',
			() => {
				peachpay_placeButtonMiniCart();
			},
		);
	});
}

window.addEventListener('message', async event => {
	if (event.origin !== location.origin) {
		return;
	}

	if (event.data === 'openModal') {
		if (peachpay_data.custom_checkout_js) {
			const frag = document.createRange().createContextualFragment(peachpay_data.custom_checkout_js);
			[...Array.from(frag.children)].forEach(child => {
				child.classList.add('pp-custom-js');
			});

			const frame = $qs<HTMLIFrameElement>('#peachpay-iframe');
			frame?.contentDocument?.body.appendChild(frag);
		}

		$qs<HTMLIFrameElement>('#peachpay-iframe', frame => {
			frame.classList.remove('hide');
			frame.contentWindow?.postMessage({event: 'UI::modalOpened'}, location.origin);
		});
		$qs('#loading-spinner-iframe')?.classList.add('hide');
		$qs('#pp-modal-overlay', overlay => {
			overlay.style.display = 'block';
		});

		document.body.style.overflow = 'hidden';
		if (/iPhone|iPad/i.test(navigator.userAgent)) {
			document.body.style.position = 'fixed';
		}

		document.body.style.touchAction = 'none';
		peachpay_hideLoadingSpinner();
		/* This setTimeout is necessary to bring the transition
		to a different thread on the browser level so that
		it utilizes the transition on all pages. */
		setTimeout(() => {
			$qs('#peachpay-iframe', frame => {
				frame.style.opacity = '1';
				frame.style.top = '0';
			});
			$qs('.pp-overlay', overlay => {
				overlay.style.background = 'rgba(0, 0, 0, 0.85)';
			});
		}, 1);
	}

	if (event.data === 'closeModal') {
		if (
			(peachpay_data.updated_currency_code && peachpay_data.updated_currency_code !== peachpay_data.currency_info?.code)
			&& peachpay_data.feature_support?.['currency_switcher_input']
			&& (peachpay_data.feature_support?.['currency_switcher_input']?.metadata?.['how_currency_defaults'] === 'billing_country'
				|| peachpay_data.feature_support?.['currency_switcher_input']?.enabled)
		) {
			window.location.reload();
		}

		closeModal();
	}

	if (event.data === 'pp-loaded') {
		loaded = true;
		if (buttonClickedBeforeLoad) {
			if (buttonClickedBeforeLoadID === 'shortcode') {
				const shortcodeInfo: Partial<IButtonConfigurationOptions['shortcodeInfo']> = {};
				if (buttonClickedBeforeLoadIsShortcodeProduct) {
					shortcodeInfo.productId = buttonClickedBeforeLoadProductID;
				}

				await sendButtonClickedMessage({
					clickID: buttonClickedBeforeLoadID,
					shortcodeInfo: shortcodeInfo as IButtonConfigurationOptions['shortcodeInfo'],
				});
			} else {
				await sendButtonClickedMessage({
					isMiniCart: buttonClickedBeforeLoadID === 'pp-button-mini',
					isFloatingButton: buttonClickedBeforeLoadID === 'pp-button-float',
					clickID: buttonClickedBeforeLoadID,
				});
			}
		}
	}
});

function closeModal() {
	if (peachpay_data.custom_checkout_js) {
		const frame = $qs<HTMLIFrameElement>('#peachpay-iframe');
		frame?.contentDocument?.querySelectorAll('.pp-custom-js').forEach(ele => {
			ele.remove();
		});
	}

	if (window.location.href.includes('?open_peachpay')) {
		window.location = `${peachpay_data.wc_cart_url}` as unknown as Location;
	}

	// This updates the background pages native cart
	if (peachpay_data.is_checkout_page) {
		jQuery(document.body).trigger('update_checkout', {update_shipping_method: false});
	} else if (peachpay_data.is_cart_page) {
		jQuery(document.body).trigger('wc_update_cart');
	}

	if ((!peachpay_data.is_cart_page || $qs('#pp-button.pp-button-float'))) {
		jQuery(document.body).trigger('wc_fragment_refresh');
	}

	$qs<HTMLIFrameElement>('#peachpay-iframe', $iframe => {
		$iframe.style.opacity = '0';
		$iframe.style.top = '50%';
	});
	$qs('.pp-overlay', $overlay => {
		$overlay.style.background = 'rgba(40, 40, 40, 0)';
	});

	if (peachpay_data.button_mobile_product_page === 'fixed') {
		const nonFloatPPButtonContainers = $qsAll('#pp-button-container:not(.pp-float-container)');

		nonFloatPPButtonContainers.forEach(buttonContainers => {
			buttonContainers.classList.remove('hide');
		});
	}

	setTimeout(() => {
		$qs<HTMLIFrameElement>('#peachpay-iframe')?.contentWindow?.postMessage({event: 'UI::modalClosed'}, location.origin);
		$qs('#pp-modal-overlay', $overlay => {
			$overlay.style.display = 'none';
		});
		document.body.style.overflow = 'auto';
		document.body.style.position = '';
		document.body.style.touchAction = 'auto';
	}, 300);
}

export function shouldLoadButtonBasedOnAccountSettings() {
	if (peachpay_data.merchant_customer_account.logged_in
		|| peachpay_data.merchant_customer_account.allow_guest_checkout
		|| peachpay_data.merchant_customer_account.checkout_login_enabled
		|| peachpay_data.merchant_customer_account.checkout_registration_enabled
	) {
		return true;
	}

	return false;
}

/**
 * Initializes the peachpay button.
 *
 * @param options Peachpay button configuration options.
 */
export function peachpay_initButton(options: IButtonConfigurationOptions) {
	peachpay_installCheckoutIframe();

	if (options.shortcodeInfo) {
		peachpay_initShortcodeButton(options);
		return;
	}

	peachpay_initButtonContainer(options);
	peachpay_initButtonEvents(options);
}

function peachpay_initShortcodeButton(options: IButtonConfigurationOptions) {
	if (!options.shortcodeInfo) {
		return;
	}

	if (!peachpay_data.test_mode) {
		$qs(`button[data-shortcode-id="${options.shortcodeInfo.shortcodeId}"]`)?.classList.remove('hide');
	}

	$qs(`button[data-shortcode-id="${options.shortcodeInfo?.shortcodeId}"]`, $button => {
		$button.addEventListener('click', async event => {
			await openPeachPay({event, options});
		});
	});
}

/**
 * Installs the PeachPay Iframe into the DOM if it has not already been added.
 */
function peachpay_installCheckoutIframe() {
	if ($qs('#pp-modal-overlay')) {
		return;
	}

	const $div = document.createElement('div');
	$div.innerHTML = peachpayModalHTMLTemplate();

	const $iframeContainer = $div.querySelector<HTMLElement>('#pp-modal-overlay');
	const $iframe = $div.querySelector<HTMLIFrameElement>('#peachpay-iframe');
	if (!$iframe) {
		return;
	}

	$iframe.addEventListener('load', () => {
		// For detecting support of peachpay alerts to prevent silent failures of messages
		$iframe?.contentWindow?.postMessage({
			event: 'init',
			// Wp_hostname is based on site_url(), which prevents bugs for subdirectory-based sites
			merchantHostname: peachpay_data.wp_hostname,
			isTestMode: peachpay_data.test_mode,
			isCartPage: peachpay_data.is_cart_page,
			isCheckoutPage: peachpay_data.is_checkout_page,
			isShopPage: peachpay_data.is_shop_page,
			isGroupedProduct: isGroupedProduct(),
			isMobile: isMobile(),
			phpData: peachpay_data,
			browserLocale: navigator.language || 'en-US',
			// This is different from the above because it is what the site
			// owner sets, whereas browserLocale is how the user configures
			// their browser.
			pageLanguage: languageCodeToLocale(getPageLanguage()),
		}, location.origin);
	});

	if (!$iframeContainer) {
		return;
	}

	// Add iframe to DOM to load.
	$qs('body')?.insertAdjacentElement('beforeend', $iframeContainer);
}

document.addEventListener('DOMContentLoaded', async () => {
	if (!shouldLoadButtonBasedOnAccountSettings()) {
		return;
	}

	if (!peachpay_data.cart) {
		peachpay_data.cart = [];
	}

	if ($qs('#pp-button.pp-button-float') && !(isMobile() && peachpay_isProductPage())) {
		$qsAll('#pp-button-container.pp-float-container').forEach(floatingButton => {
			floatingButton.classList.remove('opacity-0');
		});
		peachpay_initButton({isFloatingButton: true});
	}

	// Start the button mute observer, now that the button to observe has loaded
	const $addToCart = $qs('.single_add_to_cart_button');
	if ($addToCart) {
		peachpay_stock_observer.observe($addToCart, {attributes: true});
	}
});

function peachpay_initButtonEvents(options: IButtonConfigurationOptions): void {
	const elmtIdExt = options.isMiniCart ? '-mini' : options.isFloatingButton ? '.pp-button-float' : ':not(.pp-button-float)';
	const buttons = $qsAll<HTMLButtonElement>(`#pp-button${elmtIdExt}`);
	const paymentRequest = $qs('#pp-stripe-payment-request-wrapper');

	buttons.forEach(button => {
		// Make sure buttons only have click events attached once.
		if (button.getAttribute('pp-has-click-event') === '1') {
			return;
		}

		// Apply some dynamic styles to the button
		if (button && !options.isElementor && !button.classList.contains('pp-button-float')) {
			button.style.width = options.width ?? '100%';
			button.style.setProperty('--pp-button-background-color', peachpay_data.button_color || '#21105d');
			button.style.setProperty('--pp-button-text-color', peachpay_data.button_text_color || '#ffffff');
			if (options.borderRadius && !options.isMiniCart) {
				button.style.borderRadius = options.borderRadius.toString() + 'px';
			}

			if (paymentRequest && options.width) {
				paymentRequest.style.width = options.width;
			}
		}

		tryToMatchFontSize(button, options.isMiniCart);

		button.addEventListener('click', async event => {
			if (button.classList.contains('pp-button-float') && $qs('#pp-item-count') && parseInt($qs('#pp-item-count')?.innerHTML ?? '0') === 0) {
				$qs('#empty-cart-msg')?.classList.remove('hide');
				setTimeout(() => {
					$qs('#empty-cart-msg')?.classList.add('hide');
				}, 1000);
			} else {
				await openPeachPay({event, options});
			}
		});

		// Attach the attribute that specifies the click event is attached.
		button.setAttribute('pp-has-click-event', '1');
	});

	const itemCount = $qs('#pp-item-count');
	if (itemCount && window.matchMedia('(max-width: 900px)').matches && !peachpay_data.test_mode && parseInt($qs('#pp-item-count')?.innerHTML ?? '0') > 0) {
		$qs('.pp-float-container')?.classList.remove('hide');
	}

	// If coming from an extra feature redirect, open up PeachPay right away
	const urlParameters = new URLSearchParams(location.search);
	if (urlParameters.has('open_peachpay')) {
		// Show a loading overlay so it's clear that something is loading
		$qs('#peachpay-iframe')?.classList.add('hide');
		$qs('#loading-spinner-iframe')?.classList.remove('hide');
		$qs('#pp-modal-overlay', overlay => {
			overlay.style.display = 'flex';
		});
		$qs('body', body => {
			body.style.overflow = 'hidden';
		});

		openPeachPay({
			alternateEvent: 'pp-button',
			options,
		}).catch(() => {/**/});
	}
}

function tryToMatchFontSize(button?: HTMLButtonElement | null, isMiniCart?: boolean) {
	if (!button || isMiniCart) {
		return;
	}

	let elementToCopy;
	if (peachpay_data.is_cart_page) {
		elementToCopy = $qs('.wc-proceed-to-checkout .button.checkout-button');
	} else if (peachpay_data.is_checkout_page) {
		elementToCopy = $qs('button#place_order');
	} else {
		elementToCopy = $qs('main button[name="add-to-cart"]') ?? $qs('button[name="add-to-cart"]');
	}

	if (!elementToCopy) {
		return;
	}

	const {fontSize} = window.getComputedStyle(elementToCopy);
	if (fontSize === '0px' || fontSize === '0') {
		return;
	}

	button.style.fontSize = window.getComputedStyle(elementToCopy).fontSize;

	const buttonIcon = $qs('#button-icon-regular');
	if (window.getComputedStyle(elementToCopy).fontSize > '16px' && buttonIcon) {
		if (peachpay_data.button_icon === 'mountain') {
			buttonIcon.setAttribute('style', 'height: 15px !important');
		} else {
			buttonIcon.setAttribute('style', 'width: 24px !important; height: 24px !important;');
		}
	}
}

/**
 * Collects information about what button was click and then sends click message.
 *
 * @param context The button click context.
 */
async function openPeachPay(context: {event?: any; alternateEvent?: string; options: IButtonConfigurationOptions}) {
	let targetID = '';

	if (context.event) {
		// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
		targetID = context.event.currentTarget ? context.event.currentTarget.id : 'redirect';
	} else {
		targetID = context.alternateEvent ?? '';
	}

	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
	context.options.isFloatingButton = context.options.isFloatingButton || (context.event.currentTarget?.classList.length && context.event.currentTarget?.classList.contains('pp-button-float'));
	peachpay_showLoadingSpinner(context.options);

	if (!loaded || pendingVariationDetailRequests > 0) {
		buttonClickedBeforeLoad = true;
		if (context.options.shortcodeInfo) {
			const {productId} = context.options.shortcodeInfo;
			if (productId) {
				buttonClickedBeforeLoadIsShortcodeProduct = true;
				buttonClickedBeforeLoadProductID = productId;
			}

			buttonClickedBeforeLoadID = 'shortcode';
		// eslint-disable-next-line @typescript-eslint/no-unsafe-call
		} else if (context.event.currentTarget?.classList.length && context.event.currentTarget?.classList.contains('pp-button-float')) {
			buttonClickedBeforeLoadID = 'pp-button-float';
		} else {
			buttonClickedBeforeLoadID = targetID;
		}

		return;
	}

	context.options.clickID = targetID;
	await sendButtonClickedMessage(context.options);
}

/**
 * Initializes the PeachPay button container.
 *
 * @param { IButtonConfigurationOptions } options PeachPay button configuration options.
 */
function peachpay_initButtonContainer(options: IButtonConfigurationOptions) {
	if (options.isMiniCart || options.isFloatingButton) {
		return;
	}

	// On the product page, no position option is provided
	if (!options.isElementor) {
		$qsAll('#pp-button-container:not(.pp-float-container)', container => {
			container.style.textAlign = options.alignment ?? 'inherit';
		});
	}

	$qsAll('#pp-button-container:not(.pp-float-container)', container => {
		container.style.justifyContent = options.alignment ?? 'inherit';
	});
}

export function getPageLanguage() {
	const $html = $qs('html');
	if (!$html) {
		return 'en-US';
	}

	return $qs<HTMLHtmlElement>('html')?.lang ?? 'en-US';
}

async function peachpay_isProductInWCCart(formData: FormData) {
	let isBundleProduct = false;
	// Get variation id from variation product
	let variationId = Number.parseInt(formData.get('variation_id') as string);
	// Get variation id from bundle product that includes variation id

	formData.forEach((value, key) => {
		if (key.includes('bundle_variation')) {
			isBundleProduct = true;
			variationId = Number.parseInt(value as string);
		}
	});

	// Get product id from product ('add-to-cart' is used in simple product and bundle product)
	if (peachpay_data.cart) {
		const productId = Number.parseInt(formData.get('add-to-cart') as string);
		for (const item of peachpay_data.cart) {
			if (isBundleProduct) {
			// If current product is part of a bundle and also a variation product, check for variation id
				if (Number.parseInt(String(item.product_id)) === variationId) {
					return true;
				}
			} else if ((Number.parseInt(String(item.product_id)) === variationId || Number.parseInt(String(item.product_id)) === productId) && !item.is_part_of_bundle) {
				return true;
			}
		}
	}

	return false;
}

async function sendButtonClickedMessage(options: IButtonConfigurationOptions) {
	const isShopPage = options.isFloatingButton;
	const isProductPage = !peachpay_data.is_cart_page && !peachpay_data.is_checkout_page && !options.isMiniCart && !isShopPage && !options.isElementor;

	if (options?.shortcodeInfo?.productId || options?.shortcodeInfo?.isElementorSingleProductTemplate || isProductPage) {
		if (!peachpay_validateProductPageForm(options)) {
			peachpay_hideLoadingSpinner();
			return;
		}

		const productInWCCart = await peachpay_isProductInWCCart(peachpay_getAddToCartFormData(options));

		if (!productInWCCart) {
			const success = await peachpay_addProductToCart(options);

			peachpay_hideLoadingSpinner();

			if (!success) {
				return;
			}
		}
	}

	const data = {
		event: 'buttonClicked',
		buttonID: options.clickID,
	};

	if (peachpay_data.button_mobile_product_page === 'fixed') {
		$qs('#pp-button-container:not(.pp-float-container)')?.classList.add('hide');
	}

	const peachpay = $qs<HTMLIFrameElement>('#peachpay-iframe');
	peachpay?.contentWindow?.postMessage(data, location.origin);
}

function peachpay_validateProductPageForm(options: IButtonConfigurationOptions) {
	if (options?.shortcodeInfo && !options.shortcodeInfo?.isElementorSingleProductTemplate) {
		return true;
	}

	if (!checkValidityOf('form.cart')) {
		return false;
	}

	if (!inStockAndVariationSelected(options.isMiniCart)) {
		return false;
	}

	return true;
}

/**
 * Used to update the rest of the native woocommerce page after peachpay cart manipulations. Not async
 * to avoid blocking
 */
export function peachpay_refreshFragments(): void {
	jQuery(document.body).trigger('wc_fragment_refresh');
}

function checkValidityOf(selector: string): boolean {
	const $form = $qs<HTMLInputElement>(selector);
	let result = false;
	if ($form) {
		result = $form.checkValidity();
		$form.reportValidity();
	}

	return result;
}

function inStockAndVariationSelected(isMiniCart?: boolean) {
	// Some product pages can have multiple products listed, usually through a plugin
	const $form = $qs('form.cart');
	if (!$form) {
		return false;
	}

	const multipleProducts = Array.from($form.querySelectorAll('input.variation_id')).length > 1;

	if (selectedOutOfStockItems.size > 0 && !isMiniCart) {
		let outOfStockAlertText = '';
		if (multipleProducts) {
			outOfStockAlertText = getPluginLocaleText('Please select the remaining product options before adding this product to your cart.');
		} else {
			outOfStockAlertText = getPluginLocaleText('Sorry, this product is out of stock.');
		}

		// eslint-disable-next-line no-alert
		alert(outOfStockAlertText);
		// We need to reset this, otherwise after seeing the above alert, then selecting
		// a variation, the PeachPay window will open right away which is an unexpected
		// behavior.
		buttonClickedBeforeLoad = false;
		peachpay_hideLoadingSpinner();
		return false;
	}

	if (!variationSelected() && !isMiniCart) {
		let notSelectedAlertText;
		if (multipleProducts) {
			notSelectedAlertText = getPluginLocaleText('Please select the remaining product options before adding this bundle to your cart.');
		} else {
			notSelectedAlertText = getPluginLocaleText('Please select the remaining product options before adding this product to your cart.');
		}

		// eslint-disable-next-line no-alert
		alert(notSelectedAlertText);
		buttonClickedBeforeLoad = false;
		return false;
	}

	return true;
}

/**
 * Since this hidden input field which represents the currently selected
 * variation is changed by WooCommerce JS, there is no 'change' event that is
 * fired. This function is used primarily for that: to dispatch a change event
 * when the input's value is changed by JS. This method also preserves the
 * behavior set by other plugins' code.
 */
function setDetectChangeHandler(input: any) {
	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
	const superProps: any = Object.getPrototypeOf(input);
	const superSet = Object.getOwnPropertyDescriptor(superProps, 'value')?.set as (v: any) => void;
	const superGet = Object.getOwnPropertyDescriptor(superProps, 'value')?.get as (v: any) => void;
	const newProps = {
		get() {
			// eslint-disable-next-line @typescript-eslint/no-unsafe-return, prefer-rest-params
			return Reflect.apply(superGet, this, arguments);
		},
		set() {
			// eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call
			setTimeout(() => this.dispatchEvent(new Event('change')), 50);
			// eslint-disable-next-line prefer-rest-params
			Reflect.apply(superSet, this, arguments);
		},
	};
	Object.defineProperty(input, 'value', newProps);
}

function setCustomVariationChangeHandler(input: HTMLInputElement) {
	setDetectChangeHandler(input);

	input.addEventListener('change', (event: Event & {target: {value: string}}) => {
		if (event.target?.value === '') {
			return;
		}

		updateOutOfStockSelection(Number.parseInt(event.target.value));
	});
}

document.addEventListener('DOMContentLoaded', () => {
	const $variations = Array.from($qsAll<HTMLInputElement>('.variation_id'));
	for (const $v of $variations) {
		setCustomVariationChangeHandler($v);
	}
});

function updateOutOfStockSelection(variationId: number) {
	const variationStockRecord = collectVariationStock();

	if (variationStockRecord.size === 0) {
		// It's possible that the variation IDs collected by collectVariationStock
		// are all just the value false (not sure why some stores do this). In this case, we want
		// to do nothing instead of letting it fall into the else block below erroneously.
		return;
	}

	if (variationStockRecord.get(variationId)) {
		// Turn it into an array for easy iteration
		const selectedOutOfStockItemsArray = Array.from(selectedOutOfStockItems);
		for (let i = 0; i < selectedOutOfStockItems.size; i++) {
			const groupVariationId = selectedOutOfStockItemsArray[i];
			const group = collectVariationGroup(groupVariationId);
			if (group.includes(variationId) && (variationId !== groupVariationId) && groupVariationId) {
				// A replacement variation has been chosen for a previously selected out-of-stock item,
				// so we can remove the out-of-stock variation from the set
				selectedOutOfStockItems.delete(groupVariationId);
			}
		}
	} else {
		selectedOutOfStockItems.add(variationId);
	}
}

/**
 * Adds a product to the Woocommerce Cart.
 * @param  options PeachPay button configuration options.
 */
async function peachpay_addProductToCart(options: IButtonConfigurationOptions): Promise<boolean> {
	if (options.isMiniCart) {
		return true;
	}

	try {
		const formData = peachpay_getAddToCartFormData(options);
		const response = await fetch(`${peachpay_data.wp_home_url}/?wc-ajax=add-to-cart`, {
			method: 'POST',
			headers: {Accept: 'application/json'},
			body: formData,
		});

		if (!response.ok) {
			return false;
		}

		if (location.hostname === 'www.nomadicsupply.com') {
			const $selectedWarranty = $qs('#cps_wordpress_warranty .selected');

			if ($selectedWarranty) {
				const warrantyId = $selectedWarranty.getAttribute('warranty-attr');

				const response = await fetch(peachpay_data.wp_home_url + '/?quantity=' + formData.get('quantity') + '&add-to-cart=' + String(warrantyId), {
					method: 'POST',
					headers: {'content-type': 'application/json'},
				});

				if (!response.ok) {
					return false;
				}
			}
		}

		peachpay_refreshFragments();

		return true;
	} catch (error) {
		if (error instanceof Error) {
			captureSentryException(new Error(`Product page "add to cart" failed on ${peachpay_data.wp_home_url}. Error: ` + error.message));
			// eslint-disable-next-line no-alert
			alert(getPluginLocaleText('Sorry, something went wrong. Please refresh the page and try again.'));
		}

		return false;
	}
}

function peachpay_getAddToCartFormData(options: IButtonConfigurationOptions): FormData {
	if (options.shortcodeInfo && !options.shortcodeInfo?.isElementorSingleProductTemplate) {
		return peachpay_getShortCodeAddToCartFormData(options);
	}

	// Product page form data.
	const $form = $qs<HTMLFormElement>('form.cart');
	const formData = new FormData($form ?? undefined);

	// Simple products use the submit button as the "add-to-cart" input. This input is only submitted if the
	// button is clicked so here we add the button input data manually.
	const $submitButton = $form?.querySelector <HTMLButtonElement>('button[name="add-to-cart"]');
	if ($submitButton) {
		formData.append('add-to-cart', $submitButton.value);
	}

	return formData;
}

function isGroupedProduct() {
	return $qs('form.cart.grouped_form') !== null;
}

function variationSelected() {
	const $form = $qs('form.cart');
	if (!$form) {
		return false;
	}

	const $variations = Array.from($form.querySelectorAll<HTMLInputElement>('input.variation_id'));
	for (const variation of $variations) {
		if (variation !== null && (variation.value === '0' || variation.value === '')) {
			peachpay_hideLoadingSpinner();
			// A variation must be selected if given the option
			return false;
		}
	}

	return true;
}

// Get a map of all variations to whether that variation is in stock or not
function collectVariationStock(): Map<number, boolean> {
	const $forms = Array.from($qsAll('.variations_form'));
	const variationInStock = new Map<number, boolean>();
	for (const $form of $forms) {
		const variations = JSON.parse($form.dataset['product_variations'] ?? '[]') as Array<{variation_id: number; is_in_stock: boolean}>;
		if (!variations) {
			continue;
		}

		for (const v of variations) {
			variationInStock.set(v.variation_id, v.is_in_stock);
		}
	}

	return variationInStock;
}

// Get all the variations in the group for a single product given one variation ID
function collectVariationGroup(variationId: number | undefined) {
	const $forms = Array.from($qsAll('.variations_form'));
	const group = new Array<number>();
	// Go through all forms in case there are multiple products on the page, like for product bundles
	for (const $form of $forms) {
		let found = false;
		const variations = JSON.parse($form.dataset['product_variations'] ?? '[]') as Array<{variation_id: number}>;
		if (!variations) {
			continue;
		}

		for (const v of variations) {
			if (v.variation_id === variationId) {
				// We found the bundle product that has the given variation ID
				found = true;
			}
		}

		if (found) {
			// Now we add all the variation IDs to the group, which we can use to check
			// if the customer selected a non out-of-stock item after selecting an out-of-stock item
			for (const v of variations) {
				group.push(v.variation_id);
			}

			break;
		}
	}

	return group;
}

function peachpay_showLoadingSpinner(options: IButtonConfigurationOptions) {
	if (options.isMiniCart) {
		return;
	}

	if (shortCodeButtonIsSpinning || productButtonIsSpinning) {
		peachpay_hideLoadingSpinner();
	}

	let $ppButtons: HTMLButtonElement[] | null = null;

	if (options.shortcodeInfo) {
		$qs(`button[data-shortcode-id="${options.shortcodeInfo.shortcodeId}"] .pp-spinner`)?.classList.remove('hide');
		$qs(`button[data-shortcode-id="${options.shortcodeInfo.shortcodeId}"] .pp-button-content`)?.classList.add('hide');

		shortCodeButtonIsSpinning = true;
		shortCodeButtonSpinningID = options.shortcodeInfo.shortcodeId;

		$ppButtons = $qsAll(`button[data-shortcode-id="${options.shortcodeInfo.shortcodeId}"]`);
	} else if (options.isFloatingButton) {
		$ppButtons = $qsAll('#pp-button.pp-button-float');

		$ppButtons.forEach($ppButton => {
			$ppButton.querySelector('#loading-spinner')?.classList.remove('hide');
			$ppButton.querySelector('#pp-button-content')?.classList.add('hide');

			productButtonIsSpinning = true;
		});
	} else {
		$ppButtons = $qsAll('#pp-button:not(.pp-button-float)');

		$ppButtons.forEach($ppButton => {
			$ppButton.querySelector('#loading-spinner')?.classList.remove('hide');
			$ppButton.querySelector('#pp-button-content')?.classList.add('hide');

			productButtonIsSpinning = true;
		});
	}

	$ppButtons.forEach($ppButton => {
		$ppButton.disabled = true;
	});

	setTimeout(() => {
		// If any buttons are disabled after the set time then we need to log an infinite loading error
		const $filteredBtns = $ppButtons?.filter(button => button.disabled);

		if ($filteredBtns?.length) {
			const classlists = $filteredBtns?.reduce(
				(accumulator: string[], currentValue: HTMLElement) => {
					accumulator.push(currentValue.classList.toString());
					return accumulator;
				},
				[],
			);

			const peachpay = $qs<HTMLIFrameElement>('#peachpay-iframe');
			if (peachpay?.contentWindow) {
				peachpay?.contentWindow?.postMessage({
					event: 'captureInfiniteLoadingError',
					data: {button_classes: classlists, location: location.href},
				}, location.origin);
			} else {
				captureSentryException(new Error('pp-button stuck loading infinitely'), {
					button_classes: classlists,
					location: location.href,
					checkout_window_state: 'PeachPay Iframe not present',
					peachpay_data: JSON.stringify(peachpay_data),
				});
			}
		}
	}, 10_000);
}

export function peachpay_hideLoadingSpinner() {
	if (shortCodeButtonIsSpinning) {
		$qs(`button[data-shortcode-id="${shortCodeButtonSpinningID}"] .pp-spinner`)?.classList.add('hide');
		$qs(`button[data-shortcode-id="${shortCodeButtonSpinningID}"] .pp-button-content`)?.classList.remove('hide');

		$qs<HTMLButtonElement>(`button[data-shortcode-id="${shortCodeButtonSpinningID}"]`, $button => {
			$button.disabled = false;
		});

		shortCodeButtonIsSpinning = false;
		return;
	}

	const loadingSpinner = $qsAll('#loading-spinner');
	if (loadingSpinner) {
		loadingSpinner.forEach($el => {
			$el.classList.add('hide');
		});
	}

	const buttonContent = $qsAll('#pp-button-content');
	if (buttonContent) {
		buttonContent.forEach($el => {
			$el.classList.remove('hide');
		});
	}

	const $ppButtons = $qsAll<HTMLButtonElement>('#pp-button');
	$ppButtons.forEach($ppButton => {
		$ppButton.disabled = false;
	});

	productButtonIsSpinning = false;
}

// Watches for the WooCommerce out-of-stock behavior on products with variations and adjusts the
// style of the PeachPay checkout button accordingly.
const peachpay_stock_observer = new MutationObserver(() => {
	let needs_muted = false;

	// Checks the status of the WooCommerce cart button. If their button is disabled, PeachPay should be too.
	const checkout_button = document.getElementsByClassName('single_add_to_cart_button');
	if (checkout_button[0]) {
		const cL = checkout_button[0].classList;
		if (cL.contains('disabled')) {
			needs_muted = true;
		}
	}

	// Variable for the PeachPay button.
	const button = $qs('#pp-button');
	const stripe_wrapper = $qs('#pp-stripe-payment-request-wrapper');
	const stripe_button = $qs('#pp-stripe-payment-request-btn');
	if (button) {
		if (needs_muted) {
			button.classList.add('pp-button-mute');
		} else {
			button.classList.remove('pp-button-mute');
		}
	}

	if (stripe_button) {
		if (needs_muted) {
			stripe_wrapper?.classList.add('pp-button-mute');
			stripe_button.style.pointerEvents = 'none';
		} else {
			stripe_wrapper?.classList.remove('pp-button-mute');
			stripe_button.style.pointerEvents = 'auto';
		}
	}
});

// Per site customization to make the buttons look as native as possible
document.addEventListener('DOMContentLoaded', () => {
	peachpay_addCustomMerchantStyles();
});

export function peachpay_addCustomMerchantStyles() {
	if (peachpay_data.button_effect === 'fade') {
		peachpay_buttonFade();
	}

	if (!peachpay_data.disable_default_font_css) {
		peachpay_defaultFontCSS();
	}

	if (!peachpay_data.button_display_payment_method_icons) {
		peachpay_hide_cc_company_logos();
	}

	peachpay_customButtonCSS();
	peachpay_button_icons();
}

/**
 * This function only adds icons for non shortcode buttons unlike other custom style functions.
 * The update_buttonIcon needs shortcode ids so the shortcode icons are instead initialized in peachpay_initShortcode from shortcode.js
 */
function peachpay_button_icons() {
	if ($qs('#button-icon-regular')) {
		update_buttonIcon(peachpay_data.button_icon, 'regular');
	}
}

function peachpay_buttonFade() {
	const ppButtons = $qsAll('.pp-button:not(.elementor-pp-button)');
	ppButtons.forEach(ppButton => {
		ppButton.classList.add('pp-effect-fade');
	});
}

function peachpay_hide_cc_company_logos() {
	const ppButtons = $qsAll('.cc-company-logos:not(.elementor-cc-company-logos)');
	ppButtons.forEach(ppButton => {
		ppButton.classList.add('hide');
	});
}

function peachpay_customButtonCSS() {
	const $body = $qs('body');
	if ($body) {
		const $style = document.createElement('style');
		$style.id = 'pp-custom-css';
		$style.appendChild(document.createTextNode(peachpay_data.button_custom_css.trim()));

		$body.insertAdjacentElement('beforeend', $style);
	}
}

function peachpay_defaultFontCSS() {
	const ppButtons = $qsAll('.pp-button:not(.elementor-pp-button)');
	ppButtons.forEach(ppButton => {
		ppButton.classList.add('pp-button-default-font');
	});
}

/**
 * Assign css selectors and different svg source to the two different PeachPay
 * button types (regular and mini-cart).
 */
export function update_buttonIcon(iconValue: string, buttonType: string, _shortcodeID: number | null = null) {
	$qsAll('.button-icon-shortcode, #button-icon-regular, #button-icon-minicart', $icon => {
		if (buttonType === 'regular' || buttonType === 'shortcode') {
			$icon.classList.remove('skre-icon');
			$icon.classList.add('pp-btn-symbol');
		} else {
			$icon.classList.remove('skre-icon-mini');
			$icon.classList.add('pp-btn-symbol-mini');
		}

		$icon.classList.remove('hide');

		const beyourbag = location.hostname === 'www.beyourbag.it';

		switch (iconValue) {
			case 'lock':
				$icon.setAttribute('viewBox', '0 0 1190.000000 1280.000000');
				$icon.innerHTML = `<g ${beyourbag ? 'fill="black"' : ''} transform="translate(0.000000,1280.000000) scale(0.100000,-0.100000)" stroke="none">
				<path d="M5610 11999 c-1096 -88 -1924 -626 -2375 -1544 -80 -162 -155 -347
				-155 -380 0 -8 -4 -15 -8 -15 -4 0 -8 -8 -9 -17 -1 -19 -6 -38 -16 -63 -17
				-47 -78 -287 -91 -360 -3 -19 -8 -46 -11 -59 -2 -14 -7 -47 -10 -74 -3 -28 -8
				-53 -10 -56 -8 -13 -24 -189 -28 -313 -1 -27 -5 -48 -10 -48 -4 0 -3 -9 3 -20
				8 -14 8 -20 0 -20 -7 0 -8 -6 -1 -19 6 -10 8 -21 5 -24 -3 -3 -5 -392 -4 -865
				l1 -860 -26 -20 c-14 -11 -69 -30 -123 -43 -159 -36 -266 -74 -364 -128 -109
				-60 -149 -91 -240 -183 -206 -208 -334 -560 -353 -968 -5 -99 -4 -3022 1
				-3125 8 -175 28 -319 46 -343 7 -8 7 -12 -1 -12 -7 0 -9 -5 -4 -13 4 -6 8 -16
				9 -22 1 -5 11 -34 23 -62 11 -29 17 -53 13 -53 -4 0 -2 -4 3 -8 10 -7 37 -61
				37 -72 0 -3 6 -12 14 -21 8 -9 12 -18 9 -21 -3 -3 6 -17 20 -32 13 -14 25 -29
				25 -31 0 -14 84 -117 122 -150 24 -21 34 -32 23 -24 -18 13 -19 13 -6 -3 7
				-10 17 -18 22 -18 5 0 17 -8 27 -17 39 -37 67 -57 121 -86 175 -94 267 -121
				521 -153 111 -13 461 -15 3121 -14 3106 0 3123 0 3314 39 221 45 397 134 531
				270 161 162 238 342 269 626 12 107 15 407 15 1696 0 862 -2 1573 -4 1580 -3
				8 -7 63 -10 122 -4 60 -8 112 -10 116 -3 4 -7 27 -10 51 -17 143 -76 342 -137
				463 -30 57 -107 176 -138 211 -62 69 -159 160 -174 164 -13 3 -37 30 -38 44 0
				7 -4 4 -8 -7 -8 -19 -8 -19 -15 3 -5 14 -13 21 -21 18 -8 -3 -19 2 -25 10 -7
				8 -19 14 -26 14 -8 0 -14 5 -14 10 0 6 -7 10 -15 10 -8 0 -15 5 -15 10 0 6 -9
				10 -20 10 -11 0 -20 5 -20 11 0 5 -4 7 -10 4 -5 -3 -10 -1 -10 5 0 6 -4 8 -10
				5 -5 -3 -10 -2 -10 4 0 5 -8 8 -17 6 -10 -1 -30 4 -45 11 -15 8 -31 13 -35 13
				-4 -1 -16 1 -25 4 -46 18 -54 20 -69 23 -9 2 -27 8 -40 13 -13 5 -27 8 -30 7
				-4 0 -17 8 -30 19 -14 13 -20 14 -15 5 4 -8 -4 -2 -18 13 l-26 28 0 847 c-1
				466 -3 883 -5 927 -7 117 -14 206 -19 240 -3 24 -26 169 -40 255 -21 125 -30
				170 -36 170 -4 0 -6 10 -5 23 1 12 -1 27 -5 32 -5 6 -11 24 -13 40 -9 46 -14
				68 -25 96 -6 15 -8 29 -6 32 3 3 -2 22 -10 42 -9 21 -16 47 -16 59 0 16 -2 18
				-9 7 -7 -10 -11 -1 -16 32 -4 26 -12 47 -17 47 -6 0 -8 9 -5 20 3 11 1 20 -4
				20 -5 0 -9 7 -9 15 0 8 -4 15 -10 15 -5 0 -6 7 -3 17 5 11 3 14 -6 8 -10 -6
				-11 -2 -6 15 5 17 4 21 -5 15 -8 -5 -11 -4 -6 3 7 11 -28 93 -96 227 -22 44
				-55 107 -71 140 -17 33 -34 62 -38 65 -3 3 -16 25 -29 49 -12 24 -26 49 -31
				55 -30 36 -84 113 -105 149 -13 23 -24 38 -24 34 0 -4 -6 -2 -14 5 -8 7 -12
				16 -9 20 2 4 -7 19 -21 33 -15 14 -26 31 -26 37 0 6 -3 8 -7 5 -3 -4 -13 2
				-20 13 -8 11 -19 18 -24 15 -5 -4 -9 2 -9 12 0 11 -20 39 -45 63 -24 24 -42
				47 -39 52 3 4 -4 8 -15 8 -11 0 -22 9 -26 20 -3 11 -13 20 -21 20 -8 0 -14 7
				-14 15 0 8 -7 15 -16 15 -8 0 -12 5 -9 10 3 6 -1 10 -9 10 -9 0 -16 5 -16 11
				0 5 -3 8 -7 6 -5 -3 -14 1 -21 9 -7 8 -9 14 -5 14 4 1 -4 8 -19 16 -16 8 -28
				19 -28 24 0 6 -7 10 -15 10 -8 0 -15 5 -15 10 0 6 -5 10 -10 10 -6 0 -21 11
				-34 25 -13 14 -26 25 -30 25 -7 0 -67 41 -76 51 -3 3 -19 13 -37 22 -17 9 -37
				22 -45 29 -18 17 -59 42 -143 85 -38 19 -73 40 -76 45 -3 5 -9 9 -12 9 -4 0
				-26 8 -49 18 -374 158 -665 229 -1072 261 -150 12 -504 11 -656 -1z m475
				-1214 c103 -10 140 -15 240 -31 90 -15 283 -67 314 -85 17 -9 74 -31 79 -30
				10 4 273 -148 292 -170 3 -3 23 -18 44 -35 22 -16 49 -38 60 -49 12 -11 35
				-31 51 -43 17 -13 23 -21 15 -17 -8 4 -1 -6 16 -22 27 -24 98 -107 157 -184
				10 -13 27 -40 38 -59 11 -19 28 -48 39 -65 19 -30 73 -126 75 -135 1 -3 10
				-23 20 -45 10 -22 18 -43 17 -47 -1 -5 2 -8 6 -8 5 0 9 -6 10 -12 2 -29 29
				-88 37 -81 4 5 5 3 1 -4 -4 -7 7 -50 23 -97 16 -46 27 -87 24 -90 -3 -3 0 -12
				7 -21 7 -9 10 -18 7 -22 -4 -3 -2 -12 4 -20 9 -10 8 -13 -2 -13 -9 -1 -8 -5 5
				-15 11 -8 15 -15 8 -15 -8 0 -9 -7 -1 -27 6 -16 12 -37 14 -48 8 -49 27 -203
				29 -230 0 -16 5 -33 11 -37 7 -5 6 -8 0 -8 -6 0 -11 -9 -11 -20 0 -11 4 -20 9
				-20 5 0 4 -6 -2 -14 -9 -11 -9 -15 1 -20 10 -5 10 -7 1 -12 -19 -8 -16 -24 5
				-24 10 0 13 -3 6 -8 -16 -10 -18 -222 -3 -222 8 0 9 -4 0 -13 -8 -10 -12 -215
				-13 -707 l-3 -694 -34 -20 c-34 -20 -56 -20 -1717 -24 -1216 -3 -1683 -1
				-1688 7 -4 6 -22 11 -40 11 -69 0 -65 -59 -66 905 -1 479 -1 872 0 875 1 3 3
				32 5 65 4 65 10 117 26 210 6 33 12 71 13 85 2 14 6 32 10 41 4 9 8 27 10 41
				1 13 5 28 8 33 3 6 9 28 13 50 8 42 47 159 71 212 8 17 14 34 14 39 0 33 188
				356 247 424 36 42 197 200 239 235 110 93 398 240 507 259 7 1 28 7 47 14 58
				19 273 52 382 58 57 4 104 7 105 8 4 3 156 -3 218 -9z"/>
				</g>`;
				break;
			case 'baseball':
				$icon.setAttribute('viewBox', '0 0 496 512');
				$icon.innerHTML
					= '<path d="M368.5 363.9l28.8-13.9c11.1 22.9 26 43.2 44.1 60.9 34-42.5 54.5-96.3 54.5-154.9 0-58.5-20.4-112.2-54.2-154.6-17.8 17.3-32.6 37.1-43.6 59.5l-28.7-14.1c12.8-26 30-49 50.8-69C375.6 34.7 315 8 248 8 181.1 8 120.5 34.6 75.9 77.7c20.7 19.9 37.9 42.9 50.7 68.8l-28.7 14.1c-11-22.3-25.7-42.1-43.5-59.4C20.4 143.7 0 197.4 0 256c0 58.6 20.4 112.3 54.4 154.7 18.2-17.7 33.2-38 44.3-61l28.8 13.9c-12.9 26.7-30.3 50.3-51.5 70.7 44.5 43.1 105.1 69.7 172 69.7 66.8 0 127.3-26.5 171.9-69.5-21.1-20.4-38.5-43.9-51.4-70.6zm-228.3-32l-30.5-9.8c14.9-46.4 12.7-93.8-.6-134l30.4-10c15 45.6 18 99.9.7 153.8zm216.3-153.4l30.4 10c-13.2 40.1-15.5 87.5-.6 134l-30.5 9.8c-17.3-54-14.3-108.3.7-153.8z"></path>';
				break;
			case 'arrow':
				$icon.setAttribute('viewBox', '0 0 512 512');
				$icon.innerHTML
					= '<path d="M256 8c137 0 248 111 248 248S393 504 256 504 8 393 8 256 119 8 256 8zm113.9 231L234.4 103.5c-9.4-9.4-24.6-9.4-33.9 0l-17 17c-9.4 9.4-9.4 24.6 0 33.9L285.1 256 183.5 357.6c-9.4 9.4-9.4 24.6 0 33.9l17 17c9.4 9.4 24.6 9.4 33.9 0L369.9 273c9.4-9.4 9.4-24.6 0-34z"></path>';
				break;
			case 'mountain':
				if (buttonType === 'regular') {
					$icon.classList.remove('pp-btn-symbol');
					$icon.classList.add('skre-icon');
				} else {
					$icon.classList.remove('pp-btn-symbol-mini');
					$icon.classList.add('skre-icon-mini');
				}

				$icon.setAttribute('viewBox', '0 0 24 24');
				$icon.innerHTML
					= '<path d="M12 3.19995C11.52 3.19995 11.36 3.51995 11.04 3.83995L1.76001 19.04C1.60001 19.2 1.60001 19.52 1.60001 19.68C1.60001 20.48 2.24001 20.8 2.72001 20.8H21.28C21.92 20.8 22.4 20.48 22.4 19.68C22.4 19.36 22.4 19.36 22.24 19.04L13.12 3.83995C12.8 3.51995 12.48 3.19995 12 3.19995ZM12 5.59995L17.28 14.4H16L13.6 12L12 14.4L10.4 12L8.00001 14.4H6.56001L12 5.59995Z" />';
				break;
			case 'bag':
				$icon.setAttribute('viewBox', '0 0 512 512');
				$icon.innerHTML
					= '<path d="M320 336c0 8.84-7.16 16-16 16h-96c-8.84 0-16-7.16-16-16v-48H0v144c0 25.6 22.4 48 48 48h416c25.6 0 48-22.4 48-48V288H320v48zm144-208h-80V80c0-25.6-22.4-48-48-48H176c-25.6 0-48 22.4-48 48v48H48c-25.6 0-48 22.4-48 48v80h512v-80c0-25.6-22.4-48-48-48zm-144 0H192V96h128v32z"></path>';
				break;
			case 'shopping_cart':
				$icon.setAttribute('viewBox', '0 0 576 512');
				$icon.innerHTML
					= '<path d="M528.12 301.319l47.273-208C578.806 78.301 567.391 64 551.99 64H159.208l-9.166-44.81C147.758 8.021 137.93 0 126.529 0H24C10.745 0 0 10.745 0 24v16c0 13.255 10.745 24 24 24h69.883l70.248 343.435C147.325 417.1 136 435.222 136 456c0 30.928 25.072 56 56 56s56-25.072 56-56c0-15.674-6.447-29.835-16.824-40h209.647C430.447 426.165 424 440.326 424 456c0 30.928 25.072 56 56 56s56-25.072 56-56c0-22.172-12.888-41.332-31.579-50.405l5.517-24.276c3.413-15.018-8.002-29.319-23.403-29.319H218.117l-6.545-32h293.145c11.206 0 20.92-7.754 23.403-18.681z"></path>';
				break;
			default:
				$icon.setAttribute('viewBox', '0 0 0 0');
				$icon.classList.add('hide');
		}
	});
}

onWindowDataFetch<{country: string}, ICurrencySwitcherFeatureConfig | undefined>('pp-get-modal-currency-data', async data => {
	const response = await fetch(`${peachpay_data.wp_home_url}/?wc-ajax=pp-get-modal-currency-data`, {
		method: 'GET',
		headers: {
			credentials: 'same-origin',
			'currency-country': data.country,
		},
	});

	if (!response.ok) {
		const error = new Error(`Unable to retrieve country currency on ${peachpay_data.wp_home_url}`);
		captureSentryException(error);
		throw error;
	}

	const result = await response.json() as IResponse<ICurrencySwitcherFeatureConfig>;
	if (result.success && result.data) {
		return result.data;
	}

	return undefined;
});
